import { useCallback, useEffect, useState } from 'react';

import { SnowflakeTagRef } from '@ariksa/data-scanning/api';

import { DataScanningService } from 'api/services';

import {
  buildSkipTagOptions,
  isSameSkipTags,
  SkipTagOption,
  toSkipTags,
} from './utils';

const errorDetail = (error: any) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : undefined;
};

/**
 * Loads the account's Snowflake tags and its saved skip-tag selection, and
 * saves the selection before a sync. Added for the "skip columns that
 * already carry customer tags" option of the label sync.
 */
export const useSkipTags = (accountId?: string) => {
  const [options, setOptions] = useState<SkipTagOption[]>([]);
  const [selected, setSelected] = useState<SkipTagOption[]>([]);
  const [saved, setSaved] = useState<SnowflakeTagRef[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Without the saved settings we must not save, or we'd wipe the list.
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [tagsError, setTagsError] = useState<string>();

  useEffect(() => {
    setOptions([]);
    setSelected([]);
    setSaved([]);
    setSettingsLoaded(false);
    setTagsError(undefined);
    if (!accountId) return;

    let cancelled = false;
    setIsLoading(true);
    Promise.allSettled([
      DataScanningService.DataTypes.getSyncLabelTags({ accountId }),
      DataScanningService.DataTypes.getSyncLabelSettings({ accountId }),
    ]).then(([tagsResult, settingsResult]) => {
      if (cancelled) return;
      const available =
        tagsResult.status === 'fulfilled' ? tagsResult.value.data : [];
      const savedTags =
        settingsResult.status === 'fulfilled'
          ? settingsResult.value.data.skip_tags ?? []
          : [];
      if (tagsResult.status === 'rejected')
        setTagsError(
          errorDetail(tagsResult.reason) ??
            'Unable to load tags from Snowflake.',
        );
      const built = buildSkipTagOptions(available, savedTags);
      setOptions(built.options);
      setSelected(built.selected);
      setSaved(savedTags);
      setSettingsLoaded(settingsResult.status === 'fulfilled');
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  /** Persist the selection if it changed. Resolves to an error message on failure. */
  const save = useCallback(async (): Promise<string | undefined> => {
    if (!accountId || !settingsLoaded) return;
    const skipTags = toSkipTags(selected);
    if (isSameSkipTags(saved, skipTags)) return;
    try {
      const {
        data,
      } = await DataScanningService.DataTypes.updateSyncLabelSettings({
        accountId,
        labelSyncSettingsUpdate: { skip_tags: skipTags },
      });
      setSaved(data.skip_tags ?? []);
    } catch (error) {
      return errorDetail(error) ?? 'Unable to save skip tags.';
    }
  }, [accountId, saved, selected, settingsLoaded]);

  return {
    options,
    selected,
    setSelected,
    isLoading,
    settingsLoaded,
    tagsError,
    save,
  };
};
